'use client';

import React from 'react';

import { Container } from '@layout';
import { Heading, HeadingProps } from '@ui';

type SectionHeaderHeadingLevel = 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';

interface SectionHeaderProps {
  eyebrow?: string;
  title: string;
  description?: HeadingProps['description'] | React.ReactNode;
  /** Semantic heading level; default `h2`. */
  as?: SectionHeaderHeadingLevel;
  /**
   * Extra classes on the wrapping `<header>`.
   * Default base is `section-header`. If this string includes the token `card-header`, the base
   * becomes `card-header` instead (used when rendered from `Card`).
   */
  headerClassName?: string;
  /** Classes on the title `<Heading>` (none if omitted). */
  headingClassName?: string;
  /** Classes on the description `<p>` (none if omitted). */
  descriptionClassName?: string;
  /** Hint content to display below the title (none if omitted). */
  headingHint?: React.ReactNode;
  /** Link text to display below the title (none if omitted). */
  headingLinkText?: string;
  /** Link href to display below the title (none if omitted). */
  headingLinkHref?: string;
  /** Link target to display below the title (none if omitted). */
  headingLinkTarget?: '_blank' | '_self' | '_parent' | '_top';
  /** Link rel to display below the title (none if omitted). */
  headingLinkRel?: 'noopener' | 'noreferrer';
  /** Link as to display below the title (none if omitted). */
  headingLinkAs?: 'a' | 'button';
  /** Link class to display below the title (none if omitted). */
  headingLinkClass?: string;
  /** Link style to display below the title (none if omitted). */
  headingLinkStyle?: React.CSSProperties;
  /** Link onClick to display below the title (none if omitted). */
  headingLinkOnClick?: () => void;
  /** Link disabled to display below the title (none if omitted). */
  headingLinkDisabled?: boolean;
  /** Link position to display below the title (none if omitted). */
  headingLinkPosition?: 'start' | 'center' | 'end';
  buttonLinkText?: string;
  buttonLinkHref?: string;
  buttonLinkTarget?: '_blank' | '_self' | '_parent' | '_top';
  buttonLinkRel?: 'noopener' | 'noreferrer';
  buttonLinkAs?: 'a' | 'button';
  buttonLinkClass?: string;
  buttonLinkOnClick?: () => void;
  buttonLinkDisabled?: boolean;
  buttonLinkIcon?: React.ReactNode;
  buttonLinkIconPosition?: 'left' | 'right';
}

const SectionHeader: React.FC<SectionHeaderProps> = ({
  eyebrow,
  title,
  description,
  as = 'h2',
  headerClassName,
  headingClassName,
  descriptionClassName,
  headingHint,
  headingLinkText,
  headingLinkHref,
  headingLinkTarget,
  headingLinkRel,
  headingLinkAs = 'a',
  headingLinkClass,
  headingLinkStyle,
  headingLinkOnClick,
  headingLinkDisabled,
  headingLinkPosition = 'start',
  buttonLinkText,
  buttonLinkHref,
  buttonLinkTarget,
  buttonLinkRel,
  buttonLinkAs,
  buttonLinkClass,
  buttonLinkOnClick,
  buttonLinkDisabled,
  buttonLinkIcon,
  buttonLinkIconPosition,
}) => {
  const raw = headerClassName?.trim() ?? '';
  const parts = raw.split(/\s+/).filter(Boolean);
  const useCardHeader = parts.includes('card-header');
  const withoutCardHeaderToken = parts.filter((p) => p !== 'card-header');

  const rootClass = useCardHeader
    ? withoutCardHeaderToken.length > 0
      ? `card-header ${withoutCardHeaderToken.join(' ')}`.trim()
      : 'card-header'
    : raw
      ? `section-header ${raw}`.trim()
      : 'section-header';

  return (
    <header className={rootClass}>
      <Container className='heading-container'>
        <Heading
          eyebrow={eyebrow}
          as={as}
          className={headingClassName}
          description={description}
          descriptionClassName={descriptionClassName}
          headingHint={headingHint}
          headingLinkText={headingLinkText}
          headingLinkHref={headingLinkHref}
          headingLinkTarget={headingLinkTarget}
          headingLinkRel={headingLinkRel}
          headingLinkAs={headingLinkAs}
          headingLinkClass={headingLinkClass}
          headingLinkStyle={headingLinkStyle}
          headingLinkOnClick={headingLinkOnClick}
          headingLinkDisabled={headingLinkDisabled}
          headingLinkPosition={headingLinkPosition}
          buttonLinkText={buttonLinkText}
          buttonLinkHref={buttonLinkHref}
          buttonLinkTarget={buttonLinkTarget}
          buttonLinkRel={buttonLinkRel}
          buttonLinkAs={buttonLinkAs}
          buttonLinkClass={buttonLinkClass}
          buttonLinkOnClick={buttonLinkOnClick}
          buttonLinkDisabled={buttonLinkDisabled}
          buttonLinkIcon={buttonLinkIcon}
          buttonLinkIconPosition={buttonLinkIconPosition}
        >
          {title}
        </Heading>
      </Container>
    </header>
  );
};

export type { SectionHeaderHeadingLevel, SectionHeaderProps };
export default SectionHeader;
